import React, {
	type RefObject,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';
import { setActiveContent } from '../../state/AudioPlayerState';
import type { FlagsContainer } from '../audio-player/AudioContainer';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import type { PlaylistQuery as PlaylistQueryType } from './__generated__/PlaylistQuery.graphql';
import { FooterMessage } from './FooterMessage';
import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';

type UnavailableItem = {
	mainSource: string;
	supportingSources: string[];
	trackType: string;
};

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	contentContainer: {
		height: '406px',
		position: 'relative',
		overflowY: 'auto',
	},
	fullHeightWrapper: {
		flexGrow: 1,
	},
	playlistInnerContainer: {
		flex: 1,
		minHeight: '0px',
		position: 'relative',
		height: '100%',
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px',
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
};

const PlaylistWrapper = ({
	children,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
	audioRef,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { activeContent, clearPendingContent } = useAudioPlayerStore();

	const handleToggle = () => setIsExpanded((prev: boolean) => !prev);

	const handleMinifiedPlayerClick = () => {
		clearPendingContent();
	};

	const shouldShowMinifiedPlayer = !isExpanded && hasOtherActivePlayback;

	return (
		<Flex direction="column" xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{shouldShowMinifiedPlayer && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={activeContent.title || ''}
						byline=""
						avatarUrl={activeContent.avatarUrl || ''}
						elevated
						audioRef={audioRef}
						onClick={handleMinifiedPlayerClick}
					/>
				</Box>
			)}
			<div {...resizingHeightProps}>
				{isExpanded && (
					<Flex xcss={styles.contentContainer} direction="column">
						{/* Scroll shadow effect */}
						<Box xcss={styles.coverBorder} />
						<Flex direction="column" xcss={styles.fullHeightWrapper}>
							<Box xcss={styles.stickyShadow} />
							{children}
						</Flex>
					</Flex>
				)}
			</div>
		</Flex>
	);
};

type PlaylistInnerProps = {
	cloudId: string;
	audioRef: RefObject<HTMLAudioElement>;
	fetchKey: number;
	onIncrementFetchKey: () => void;
	flags: FlagsContainer;
};

const PlaylistInner = ({
	cloudId,
	audioRef,
	flags,
	fetchKey,
	onIncrementFetchKey,
}: PlaylistInnerProps) => {
	const [isFooterDismissed, setIsFooterDismissed] = useState(false);
	const shouldShowFooterMessageRef = useRef(false);
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const { activeContent } = useAudioPlayerStore();
	const { removeTrack, addTrack } = useContextMenuActions();

	const data = useLazyLoadQuery<PlaylistQueryType>(
		graphql`
			query PlaylistQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainSource
							mainContent {
								id
								title
								type
								status
								subType
								space {
									key
								}
								history {
									createdBy {
										displayName
										profilePicture {
											path
										}
									}
								}
								properties(keys: ["emoji-title-published"]) {
									nodes {
										value
									}
								}
							}
							supportingSources
						}
					}
				}
			}
		`,
		{ cloudId },
		{
			fetchPolicy: 'network-only',
			fetchKey,
		},
	);

	const { playlistItems: serverPlaylistItems, unavailableItems } = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];
		const filtered: PlaylistItemData[] = [];
		const unavailable: UnavailableItem[] = [];

		tracks.forEach((track: any) => {
			const mainContent = track.mainContent;
			const hasMainContent = !!mainContent;
			const hasValidStatus = mainContent?.status === 'current' || mainContent?.status === 'draft';

			if (hasMainContent && hasValidStatus) {
				const creator = mainContent.history?.createdBy;
				const avatarUrl = creator?.profilePicture?.path || undefined;
				const item: PlaylistItemData = {
					id: track.id,
					title: mainContent.title || undefined,
					trackType: track.type,
					author: creator?.displayName || undefined,
					avatarUrl,
					spaceKey: mainContent.space?.key || '',
					contentType: mainContent.type || '',
					isLive: !!(mainContent.subType === 'live'),
					emojiId: mainContent.properties?.nodes?.[0]?.value || undefined,
					contentId: mainContent.id,
					mainSource: track.mainSource,
					supportingSources: track.supportingSources || [],
				};
				filtered.push(item);
			} else {
				const unavailableItem: UnavailableItem = {
					mainSource: track.mainSource,
					supportingSources: track.supportingSources || [],
					trackType: track.type,
				};
				unavailable.push(unavailableItem);
			}
		});

		return { playlistItems: filtered, unavailableItems: unavailable };
	}, [data]);

	const shouldShowFooterMessage = shouldShowFooterMessageRef.current && !isFooterDismissed;

	// Remove unavailable items from the playlist
	useEffect(() => {
		if (unavailableItems.length === 0) {
			return;
		}
		shouldShowFooterMessageRef.current = true;
		Promise.allSettled(
			unavailableItems.map((item) =>
				removeTrack(item.mainSource, item.supportingSources, item.trackType as TrackType),
			),
		);
	}, [unavailableItems, removeTrack]);
	const [optimisticPlaylistItems, setOptimisticPlaylistItems] = useState<PlaylistItemData[]>([]);
	const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false);

	useEffect(() => {
		if (!isOptimisticUpdate) {
			setOptimisticPlaylistItems(serverPlaylistItems);
		}
	}, [serverPlaylistItems, isOptimisticUpdate]);

	const currentPlaylistItems = isOptimisticUpdate ? optimisticPlaylistItems : serverPlaylistItems;

	const currentItem = useMemo(() => {
		if (!activeContent?.contentId) {
			return null;
		}
		return currentPlaylistItems.find((item) => item.contentId === activeContent.contentId) || null;
	}, [activeContent?.contentId, currentPlaylistItems]);

	const handleItemClick = useCallback(
		(item: PlaylistItemData) => {
			// Add the track to the top of the playlist
			addTrack(item.mainSource, item.supportingSources, item.trackType).catch(() => {
				// Silently handle errors - the track will still play even if add fails
				// TODO: analytics
			});
			setActiveContent({
				contentId: item.contentId,
				spaceKey: item.spaceKey || '',
				contentType: item.contentType || '',
				title: item.title,
				author: item.author,
				avatarUrl: item.avatarUrl || '',
			});
			fetchAudio();
		},
		[addTrack, fetchAudio],
	);

	const handleOptimisticReorder = useCallback(
		(fromIndex: number, toIndex: number) => {
			const newItems = [...currentPlaylistItems];
			const [movedItem] = newItems.splice(fromIndex, 1);
			newItems.splice(toIndex, 0, movedItem);

			setOptimisticPlaylistItems(newItems);
			setIsOptimisticUpdate(true);

			return newItems;
		},
		[currentPlaylistItems],
	);

	const handleReorderSuccess = useCallback(() => {
		setIsOptimisticUpdate(false);
		onIncrementFetchKey();
	}, [onIncrementFetchKey]);

	const handleReorderError = useCallback(() => {
		setIsOptimisticUpdate(false);
		setOptimisticPlaylistItems(serverPlaylistItems);
	}, [serverPlaylistItems]);

	return (
		<Flex direction="column" xcss={styles.playlistInnerContainer}>
			{currentPlaylistItems.length === 0 ? (
				<PlaylistEmptyView />
			) : (
				<PlaylistView
					items={currentPlaylistItems}
					currentItem={currentItem}
					onItemClick={handleItemClick}
					audioRef={audioRef}
					flags={flags}
					onOptimisticReorder={handleOptimisticReorder}
					onReorderSuccess={handleReorderSuccess}
					onReorderError={handleReorderError}
				/>
			)}
			{shouldShowFooterMessage && <FooterMessage onClose={() => setIsFooterDismissed(true)} />}
		</Flex>
	);
};

type PlaylistProps = {
	cloudId: string;
	defaultExpanded?: boolean;
	audioRef: RefObject<HTMLAudioElement>;
	flags: FlagsContainer;
};

export const Playlist = ({
	cloudId,
	defaultExpanded,
	audioRef,
	flags,
}: PlaylistProps): React.JSX.Element => {
	const { pendingContent } = useAudioPlayerStore();
	const [fetchKey, setFetchKey] = useState(0);

	const handleIncrementFetchKey = useCallback(() => {
		setFetchKey((prev) => prev + 1);
	}, []);

	return (
		<PlaylistWrapper
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={pendingContent !== null}
			audioRef={audioRef}
		>
			<ErrorBoundary
				key={fetchKey}
				fallbackRender={(props) => (
					<PlaylistErrorView {...props} onRetry={handleIncrementFetchKey} />
				)}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner
						cloudId={cloudId}
						audioRef={audioRef}
						flags={flags}
						fetchKey={fetchKey}
						onIncrementFetchKey={handleIncrementFetchKey}
					/>
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
